import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './SplashPage.css';
import logo from '../assets/GrabTime_Clean_Transparent.png';
import logoBronze from '../assets/GrabTime_Clean_Transparent_bronze.png';
import heroImage from '../assets/hero-atmosphere.png';

const SplashPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const pillars = [
    {
      number: '01',
      eyebrow: 'Small Tables',
      title: '4-6 guests at the table.',
      description: "You'll join 3-5 peers, so the conversation stays personal, easy to enter, and worth continuing.",
    },
    {
      number: '02',
      eyebrow: 'Right Peers',
      title: 'Chosen for fit, not volume.',
      description: 'We bring together peers with shared context, complementary perspective, and a real reason to know one another.',
    },
    {
      number: '03',
      eyebrow: 'Preparation',
      title: 'You arrive warm.',
      description: 'Before dinner, you receive guest notes so the table never feels random and the first hello is already easy.',
    },
  ];

  const curatedConnections = [
    {
      number: '01',
      title: 'Peers in a similar season',
      description: 'People navigating adjacent questions, decisions, and opportunities.',
    },
    {
      number: '02',
      title: 'Complementary perspectives',
      description: 'People who add range, perspective, and shared standards without losing relevance.',
    },
    {
      number: '03',
      title: 'Someone a few chapters ahead',
      description: 'The kind of experienced perspective that sharpens the evening without turning it into a program.',
    },
  ];

  const workflowSteps = [
    {
      eyebrow: 'Context',
      title: 'We learn your world.',
      description: 'Your concierge learns what you are building, what matters to you, and the kind of company you genuinely enjoy.',
    },
    {
      eyebrow: 'Table shaping',
      title: 'We assemble the right mix.',
      description: 'We shape each dinner around 4-6 guests whose context, chemistry, and timing make sense together.',
    },
    {
      eyebrow: 'Guest notes',
      title: 'Everyone walks in warm.',
      description: 'Everyone receives guest notes before dinner, so the evening feels intentional from the first hello.',
    },
    {
      eyebrow: 'After dinner',
      title: 'The evening can continue naturally.',
      description: 'We help the right conversations carry forward with a light, gracious touch.',
    },
  ];

  const behindMatchSteps = [
    {
      title: 'Shared context',
      description: 'Enough overlap for conversation to start easily, without making everyone identical.',
      trust: 'Matched with purpose, never at random.',
    },
    {
      title: 'Complementary perspective',
      description: 'Different angles, similar standards, and room for a genuinely interesting exchange.',
      trust: 'Chosen for relevance, not volume.',
    },
    {
      title: 'Timing and location',
      description: 'A dinner you could actually say yes to, in a city and time that fits real life.',
      trust: 'Built around real availability.',
    },
    {
      title: 'Follow-through potential',
      description: 'Guests you would be glad to know after the bill arrives, not just for one evening.',
      trust: 'Designed for conversations worth continuing.',
    },
  ];

  const calendarAssurances = [
    {
      number: '01',
      title: 'Read-only access',
      description: 'We only see busy/free blocks so we know when you are available. Event titles, locations, and notes stay private. Always.',
    },
    {
      number: '02',
      title: 'Nothing is changed',
      description: 'GrabTime will never edit, move, or add to your calendar. You stay in control.',
    },
    {
      number: '03',
      title: 'Disconnect anytime',
      description: 'Your Google OAuth token can be revoked with one click. You own your time.',
    },
  ];

  return (
    <div className="splash-page">
      <header className={`splash-nav ${isScrolled ? 'nav-scrolled' : ''}`}>
        <div className="nav-inner">
          <Link to="/" className="nav-logo">
            <img src={isScrolled ? logo : logoBronze} alt="GrabTime" />
          </Link>
          <nav className="nav-links">
            <a href="#workflow">How We Shape the Table</a>
            <a href="#privacy">Privacy</a>
            <a href="#stories">Stories</a>
          </nav>
          <div className="nav-actions">
            <Link to="/login" className="nav-sign-in">Sign In</Link>
          </div>
        </div>
      </header>

      <main>
        <section className="hero-section">
          <div className="hero-bg">
            <img src={heroImage} alt="" aria-hidden="true" />
            <div className="hero-overlay" />
          </div>
          <div className="hero-content">
            <h1 className="hero-headline">Not networking.</h1>
            <p className="hero-descriptor">Just real connection with the right peers.</p>
            <p className="hero-subline">
              Small, curated dinners of 4-6 guests, thoughtfully placed around shared context and genuine reasons to know one another.
            </p>
            <div className="hero-cta-stack">
              <Link to="/register" className="hero-cta">Request a seat at the table</Link>
              
            </div>
          </div>
        </section>

        <section className="what-it-is-strip" aria-label="What GrabTime is">
          <div className="what-it-is-inner">
            <span className="what-it-is-item">You'll join 3-5 peers</span>
            <span className="what-it-is-divider" aria-hidden="true" />
            <span className="what-it-is-item">Thoughtful placement</span>
            <span className="what-it-is-divider" aria-hidden="true" />
            <span className="what-it-is-item">Guest notes before dinner</span>
          </div>
        </section>

        <section className="pillars-section">
          <div className="pillars-inner">
            {pillars.map((pillar) => (
              <div className="pillar-item" key={pillar.number}>
                <span className="pillar-number">{pillar.number}</span>
                <div className="pillar-text">
                  <span className="pillar-eyebrow">{pillar.eyebrow}</span>
                  <h3 className="pillar-title">{pillar.title}</h3>
                  <p className="pillar-description">{pillar.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="quote-section" id="stories">
          <span className="section-eyebrow">Why It Feels Different</span>
          <blockquote className="editorial-quote">
            "The right table does more than introduce people. It changes the quality of the conversation."
          </blockquote>
          <p className="editorial-body">
            GrabTime is built for thoughtful dinners with the peers you would actually want to know, not calendar noise or a volume of catch-ups to sort through later.
          </p>
        </section>

        <section className="connections-section">
          <span className="section-eyebrow">Who You Might Meet</span>
          <h2 className="connections-headline">The kind of company that belongs at your table.</h2>
          <div className="connections-list">
            {curatedConnections.map((item) => (
              <div className="connections-item" key={item.number}>
                <span className="connections-number">{item.number}</span>
                <div>
                  <h3 className="connections-item-title">{item.title}</h3>
                  <p className="connections-item-desc">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="protocol-section" id="workflow">
          <div className="protocol-layout">
            <div className="protocol-sticky">
              <span className="section-eyebrow">How We Shape the Table</span>
              <h2 className="protocol-headline">How the right dinner comes together.</h2>
            </div>
            <div className="protocol-steps">
              <div className="protocol-line" aria-hidden="true" />
              {workflowSteps.map((step, index) => (
                <div className="protocol-step" key={step.eyebrow}>
                  <span className="step-number">{(index + 1).toString().padStart(2, '0')}</span>
                  <span className="step-eyebrow">{step.eyebrow}</span>
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-body">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="protocol-mobile">
            <span className="section-eyebrow">How We Shape the Table</span>
            <h2 className="protocol-headline">How the right dinner comes together.</h2>
            <div className="protocol-mobile-steps">
              {workflowSteps.map((step, index) => (
                <div className="protocol-mobile-step" key={step.eyebrow}>
                  <span className="step-number">{(index + 1).toString().padStart(2, '0')}</span>
                  <div>
                    <span className="step-eyebrow">{step.eyebrow}</span>
                    <h3 className="step-title">{step.title}</h3>
                    <p className="step-body">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="briefing-section">
          <div className="briefing-inner">
            <span className="section-eyebrow light">Before We Place You</span>
            <h2 className="briefing-headline">What shapes a good table</h2>
            <p className="briefing-subtitle">A thoughtful dinner starts with more than a title and a bio.</p>
            <div className="briefing-list">
              {behindMatchSteps.map((step) => (
                <div className="briefing-item" key={step.title}>
                  <div className="briefing-item-text">
                    <h3 className="briefing-item-title">{step.title}</h3>
                    <p className="briefing-item-desc">{step.description}</p>
                  </div>
                  <span className="briefing-trust">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    {step.trust}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="privacy-section" id="privacy">
          <span className="section-eyebrow">Private by design</span>
          <h2 className="privacy-headline">Calendar signals, not calendar details.</h2>
          <p className="privacy-subtitle">We use only what helps us find a workable dinner time. Nothing more.</p>
          <div className="privacy-list">
            {calendarAssurances.map((item) => (
              <div className="privacy-item" key={item.number}>
                <span className="privacy-number">{item.number}</span>
                <div>
                  <h4 className="privacy-item-title">{item.title}</h4>
                  <p className="privacy-item-desc">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="final-cta-section">
          <div className="final-cta-inner">
            <h2 className="final-cta-headline">A better circle starts with one well-chosen table.</h2>
            <div className="final-cta-stack">
              <Link to="/register" className="final-cta-btn">Request a seat at the table</Link>
            </div>
          </div>
        </section>
      </main>

      <footer className="splash-footer">
        <div className="footer-inner">
          <img src={logo} alt="GrabTime" className="footer-logo" />
          <p className="footer-product-line">Small, curated dinners with the right peers.</p>
          <p className="footer-tagline">Thoughtful placement. Guest notes before dinner. Real follow-through.</p>
          <div className="footer-links">
            <a href="#workflow">How We Shape the Table</a>
            <a href="#privacy">Privacy</a>
            <a href="#stories">Stories</a>
            <Link to="/register">Request a seat at the table</Link>
          </div>
          <div className="footer-social">
            <a
              href="https://www.instagram.com/grabtime.ai/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Instagram
            </a>
            <a
              href="https://www.linkedin.com/company/grabtime-ai/"
              target="_blank"
              rel="noopener noreferrer"
            >
              LinkedIn
            </a>
          </div>
          <div className="footer-legal">
            <Link to="/terms">Terms of Use</Link>
            <Link to="/privacy">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SplashPage;
